import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, MapPinned } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useExternalLink } from "@/contexts/ExternalLinkContext";
import { legalTranslations } from "@/i18n/legalTranslations";

const RESERVE_URL = "https://www.foodbooking.com/ordering/?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538&reservation=true";
const GOOGLE_MAPS_URL = "https://maps.google.com/?cid=8868003826117860262";

const navLinks = [
  { to: "/", key: "nav_home" },
  { to: "/menu", key: "nav_menu" },
  { to: "/galeria", key: "nav_gallery" },
  { to: "/nosotros", key: "nav_about" },
  { to: "/blog", key: "nav_blog" },
  { to: "/restaurante-japones-barcelona", key: "nav_restaurant" },
  { to: "/contacto", key: "nav_contact" },
];

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const socialLinks = [
  { href: "https://www.instagram.com/chibikokoro/", icon: Instagram, label: "Instagram" },
  { href: "https://www.facebook.com/chibikokoro.kokoro", icon: Facebook, label: "Facebook" },
  { href: GOOGLE_MAPS_URL, icon: MapPinned, label: "Google Maps" },
];

const Footer = () => {
  const { t, lang } = useLanguage();
  const { openLink } = useExternalLink();
  const lt = legalTranslations[lang];

  return (
    <footer className="bg-kuro text-shiro">
      {/* Map embed */}
      <div className="w-full h-64 md:h-80">
        <iframe
          title="Ubicación Chibikokoro"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2994.074!2d2.1324!3d41.3756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a499ce9f9557bd%3A0x7b117a7ec28f5ba6!2sSushi%20Chibi%20Kokoro!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
          className="w-full h-full border-0 grayscale contrast-125 opacity-60"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      <div className="max-w-7xl mx-auto section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-10">
          {/* 1 — Navigation */}
          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-shiro/60 mb-6 font-body">{t("footer_navigation")}</h4>
            <ul className="space-y-3 text-sm font-body">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-shiro/70 hover:text-shiro transition-colors">
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 2 — Legal */}
          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-shiro/60 mb-6 font-body">{t("footer_legal")}</h4>
            <ul className="space-y-3 text-sm font-body">
              <li>
                <Link to="/aviso-legal" className="text-shiro/70 hover:text-shiro transition-colors">
                  {lt.footer_legal_notice}
                </Link>
              </li>
              <li>
                <Link to="/politica-de-privacidad" className="text-shiro/70 hover:text-shiro transition-colors">
                  {lt.footer_privacy_policy}
                </Link>
              </li>
              <li>
                <Link to="/politica-de-cookies" className="text-shiro/70 hover:text-shiro transition-colors">
                  {lt.footer_cookie_policy}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
                  className="text-shiro/70 hover:text-shiro transition-colors"
                >
                  {lt.cookie_manage}
                </button>
              </li>
            </ul>
          </div>

          {/* 3 — Contact */}
          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-shiro/60 mb-6 font-body">{t("footer_contact")}</h4>
            <ul className="space-y-4 text-sm font-body">
              <li className="flex items-start gap-3 text-shiro/70">
                <MapPin className="w-4 h-4 mt-0.5 text-aka shrink-0" />
                <span>Carrer de Béjar, 64, Sants-Montjuïc<br />08014 Barcelona, España</span>
              </li>
              <li>
                <a href="tel:+34931463045" className="flex items-center gap-3 text-shiro/70 hover:text-shiro transition-colors">
                  <Phone className="w-4 h-4 text-aka shrink-0" />
                  +34 931 46 30 45
                </a>
              </li>
              <li>
                <a href="tel:+34653538328" className="flex items-center gap-3 text-shiro/70 hover:text-shiro transition-colors">
                  <Phone className="w-4 h-4 text-aka shrink-0" />
                  +34 653 53 83 28
                </a>
              </li>
              <li>
                <a href="mailto:sushichibikokoro@gmail.com" className="flex items-center gap-3 text-shiro/70 hover:text-shiro transition-colors">
                  <Mail className="w-4 h-4 text-aka shrink-0" />
                  sushichibikokoro@gmail.com
                </a>
              </li>
            </ul>
            {/* Hours */}
            <div className="mt-6">
              <h4 className="text-xs tracking-[0.3em] uppercase text-shiro/60 mb-4 font-body">{t("footer_hours")}</h4>
              <div className="flex items-start gap-3 text-sm font-body text-shiro/70">
                <Clock className="w-4 h-4 mt-0.5 text-aka shrink-0" />
                <div>
                  <p>{t("contact_days")}</p>
                  <p>{t("contact_schedule")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 4 — Follow Us (Cityrama-style circular icons) */}
          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-shiro/60 mb-6 font-body">{t("footer_social")}</h4>
            <div className="flex items-center gap-4">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="group w-11 h-11 rounded-full border border-shiro/20 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:border-aka hover:shadow-[0_0_16px_hsla(352,82%,45%,0.25)]"
                >
                  <s.icon className="w-[18px] h-[18px] text-shiro/70 transition-colors duration-300 group-hover:text-aka" />
                </a>
              ))}
            </div>
            {/* Brand tagline */}
            <div className="mt-8">
              <h3 className="font-display text-2xl font-semibold mb-1">
                <span className="text-aka">Chibi</span>kokoro
              </h3>
              <p className="text-shiro/60 text-xs tracking-[0.3em] uppercase italic font-body">Japanese Restaurant</p>
              <p className="text-shiro/50 text-sm leading-relaxed font-body mt-3">{t("footer_desc")}</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-shiro/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-shiro/50 font-body">
            © {new Date().getFullYear()} Chibikokoro – Japanese Restaurant. {t("footer_rights")}
          </p>
          <div className="flex gap-6 text-xs text-shiro/50 font-body">
            <Link to="/contacto" className="hover:text-shiro/70 transition-colors">{t("nav_contact")}</Link>
            <button onClick={() => openLink(RESERVE_URL)} className="hover:text-shiro/70 transition-colors">{t("nav_reservations")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
